import { useState, useEffect, createContext, useContext } from 'react';
import '@/index.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import { BrandProvider, useBrand } from './lib/brandContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import BusinessDashboard from './pages/BusinessDashboard';
import SalespersonDashboard from './pages/SalespersonDashboard';
import Profile from './pages/Profile';
import SalespeopleDirectory from './pages/SalespeopleDirectory.jsx';
import SalespersonProfile from './pages/SalespersonProfile.jsx';
import AdminConsole from './pages/admin/AdminConsole.jsx';
import Leaderboard from './pages/Leaderboard.jsx';
import CompareSalespeople from './pages/CompareSalespeople';

// Initialize Sentry
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT || 'dev',
    release: process.env.REACT_APP_RELEASE_SHA || 'dev',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Global axios interceptor for 401 errors
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, [token]);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    setToken(token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, refreshUser, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children, allowedRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRole && user.role !== allowedRole) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const Home = () => {
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      if (user.role === 'BUSINESS') {
        window.location.href = '/business/dashboard';
      } else if (user.role === 'ADMIN') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/salesperson/dashboard';
      }
    }
  }, [user]);

  return <Navigate to="/login" replace />;
};

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog>
      <div className="min-h-screen bg-brand-bg text-brand-text relative">
        {/* Subtle gold glow */}
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(600px_300px_at_20%_-10%,rgba(212,175,55,.12),transparent),radial-gradient(500px_250px_at_90%_-20%,rgba(195,154,46,.10),transparent)]" />
        
        <AuthProvider>
          <BrandProvider>
            <SplashGate>
              <BrowserRouter>
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route
                path="/business/dashboard"
                element={
                  <ProtectedRoute allowedRole="BUSINESS">
                    <BusinessDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salesperson/dashboard"
                element={
                  <ProtectedRoute allowedRole="SALESPERSON">
                    <SalespersonDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salespeople"
                element={
                  <ProtectedRoute>
                    <SalespeopleDirectory />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/salespeople/:id"
                element={
                  <ProtectedRoute>
                    <SalespersonProfile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminConsole />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <Leaderboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/compare"
                element={
                  <ProtectedRoute allowedRole="BUSINESS">
                    <CompareSalespeople />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </BrowserRouter>
          </SplashGate>
          </BrandProvider>
        </AuthProvider>
      </div>
    </Sentry.ErrorBoundary>
  );
}

const SplashGate = ({ children }) => {
  const { logoUrl, brandLoading } = useBrand();
  const { loading: authLoading } = useAuth();
  const [fadeOut, setFadeOut] = useState(false);
  const [hidden, setHidden] = useState(false);

  const appReady = !brandLoading && !authLoading;

  useEffect(() => {
    if (appReady) {
      // Start fade-out after a brief hold so user sees the logo
      const holdTimer = setTimeout(() => setFadeOut(true), 1200);
      return () => clearTimeout(holdTimer);
    }
  }, [appReady]);

  useEffect(() => {
    if (fadeOut) {
      const hideTimer = setTimeout(() => setHidden(true), 600);
      return () => clearTimeout(hideTimer);
    }
  }, [fadeOut]);

  if (hidden) return children;

  return (
    <>
      {/* Splash overlay */}
      <div
        data-testid="splash-screen"
        className={`fixed inset-0 z-[9999] bg-brand-bg flex items-center justify-center transition-opacity duration-500 ${
          fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt="Logo"
            className="max-h-24 max-w-[260px] object-contain animate-[fadeInUp_0.6s_ease-out_forwards]"
            data-testid="splash-logo"
          />
        ) : (
          <div className="animate-[fadeInUp_0.6s_ease-out_forwards]">
            <div className="w-16 h-16 rounded-2xl bg-brand-gold/20 flex items-center justify-center">
              <div className="w-8 h-8 rounded-full bg-brand-gold/60" />
            </div>
          </div>
        )}
      </div>
      {/* Render children behind so they start loading */}
      <div className={fadeOut ? '' : 'invisible'}>{children}</div>
    </>
  );
};

const ErrorFallback = () => (
  <div className="min-h-screen bg-brand-bg flex items-center justify-center p-4">
    <div className="text-center">
      <h1 className="text-2xl font-display text-brand-text mb-4">Something went wrong</h1>
      <p className="text-brand-muted mb-6">We've been notified and are looking into it.</p>
      <button
        onClick={() => window.location.reload()}
        className="bg-brand-gold text-black px-6 py-2 rounded-xl"
      >
        Reload Page
      </button>
    </div>
  </div>
);

export default App;
